import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Hammer, Cog, Layers, ShieldCheck, Factory, Gauge } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

const HERO_BG = "https://images.pexels.com/photos/15288012/pexels-photo-15288012.jpeg";
const LEGACY_IMG = "https://images.pexels.com/photos/12381213/pexels-photo-12381213.jpeg";
const CAP_INJECTION = "https://images.unsplash.com/photo-1727292485858-588c7652ad69";
const CAP_MATERIALS = "https://images.unsplash.com/photo-1586013286823-5ba3576ea8d2";

const Overline = ({ children, testId }) => (
  <div data-testid={testId} className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0dd3ce] mb-4">
    {children}
  </div>
);

const Hero = () => (
  <section data-testid="hero-section" className="relative min-h-[88vh] flex items-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={HERO_BG} alt="industrial moulding floor" className="w-full h-full object-cover opacity-30 grayscale" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#062632] via-[#062632]/85 to-[#062632]/40" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_60%,#062632)]" />
    </div>
    <div className="relative z-10 px-6 md:px-12 lg:px-20 max-w-7xl w-full py-24">
      <Overline testId="hero-overline">Sagar Plastics · Est. legacy · A3 Sagar Polymers</Overline>
      <h1 data-testid="hero-title" className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-slate-50 max-w-4xl leading-[1.02]">
        Two decades of moulding heritage.<br/>
        <span className="text-[#0dd3ce]">Now engineered for industrial scale.</span>
      </h1>
      <p data-testid="hero-subtitle" className="mt-7 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
        From a trusted Mumbai workshop to a high-tonnage industrial Gala — we deliver injection and hand-moulded
        plastic components built on grit, precision, and twenty-plus years of B2B trust.
      </p>
      <div className="mt-10 flex flex-wrap gap-4">
        <Link to="/rfq" data-testid="hero-cta-rfq" className="group inline-flex items-center gap-3 bg-[#0dd3ce] text-[#03161D] font-semibold px-7 py-4 hover:bg-[#5ce7e3] transition-colors uppercase tracking-wider text-sm">
          Request a Quote <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
        <a href="#capabilities" data-testid="hero-cta-explore" className="inline-flex items-center gap-3 border border-[#0e4555] text-slate-200 px-7 py-4 hover:bg-slate-800 transition-colors uppercase tracking-wider text-sm">
          Explore Capabilities
        </a>
      </div>
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
        {[
          { k: "20+", l: "Years of moulding mastery" },
          { k: "High-T", l: "Industrial tonnage" },
          { k: "Mumbai", l: "Engineered in India" },
        ].map((s, i) => (
          <div
            key={i}
            data-testid={`metric-card-${i}`}
            className="border border-[#0dd3ce]/30 bg-[#062632]/40 backdrop-blur-sm px-6 py-5 hover:border-[#0dd3ce]/70 transition-colors"
          >
            <div className="font-display text-3xl sm:text-4xl font-extrabold text-[#0dd3ce]">{s.k}</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-300/80 mt-2 font-medium">{s.l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Legacy = () => (
  <section id="legacy" data-testid="legacy-section" className="px-6 md:px-12 lg:px-20 py-24 max-w-7xl mx-auto">
    <div className="grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <Overline>Our Story</Overline>
        <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-100 leading-[1.05]">
          A Mumbai workshop. <br/>A modern industrial floor. <br/>
          <span className="text-[#0dd3ce]">One unbroken thread of trust.</span>
        </h2>
        <div className="mt-8 space-y-5 text-slate-400 leading-relaxed max-w-2xl">
          <p>
            For over <span className="text-slate-200 font-semibold">20 years</span>, Sagar Plastics has shaped polymer parts
            by hand and machine for some of Mumbai's most demanding B2B clients. Reliability that doesn't slip a deadline,
            relationships measured in decades — that is the foundation we were built on.
          </p>
          <p>
            <span className="text-slate-200 font-semibold">A3 Sagar Polymers</span> is the next chapter — a new industrial Gala
            engineered for high-tonnage injection moulding, dedicated tool rooms, and global operational standards. The grit
            of traditional manufacturing, refined for modern engineering.
          </p>
          <p>
            We don't sell components. We protect supply chains.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-3 gap-5">
          {[
            { icon: ShieldCheck, t: "Decade-tested trust", d: "Repeat B2B relationships" },
            { icon: Factory, t: "Industrial Gala", d: "High-tonnage capacity" },
            { icon: Gauge, t: "Engineered output", d: "Spec-grade tolerances" },
          ].map(({ icon: Icon, t, d }, i) => (
            <div key={i} className="border border-[#0a3a47] p-5 bg-[#0a3845]/40 hover:border-[#0dd3ce]/40 transition-colors">
              <Icon className="w-5 h-5 text-[#0dd3ce] mb-3" />
              <div className="text-sm font-semibold text-slate-200">{t}</div>
              <div className="text-xs text-slate-500 mt-1">{d}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-5 relative">
        <div className="relative aspect-[4/5] overflow-hidden border border-[#0a3a47]">
          <img src={LEGACY_IMG} alt="legacy workshop" className="w-full h-full object-cover grayscale brightness-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#062632] via-[#062632]/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0dd3ce]">Heritage · Mumbai</div>
            <div className="font-display text-2xl text-slate-100 mt-1">Built by hand. Scaled by engineering.</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CAPABILITIES = [
  {
    icon: Cog,
    title: "Injection Moulding",
    desc: "High-tonnage industrial machines for consistent, high-volume polymer components with spec-grade dimensional control.",
    img: CAP_INJECTION,
  },
  {
    icon: Hammer,
    title: "Hand Moulding",
    desc: "Two decades of artisan moulding expertise for low-volume, complex, and prototyping work where machines fall short.",
    img: LEGACY_IMG,
  },
  {
    icon: Layers,
    title: "Custom Plastic Components",
    desc: "End-to-end engineering — material selection, tooling, sampling, and production — built around your drawing.",
    img: CAP_MATERIALS,
  },
];

const Capabilities = () => (
  <section id="capabilities" data-testid="capabilities-section" className="px-6 md:px-12 lg:px-20 py-24 bg-[#03161D] border-y border-[#082530]">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <Overline>Capabilities</Overline>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-100 tracking-tight max-w-2xl">
            Engineered for procurement that can't afford slip-ups.
          </h2>
        </div>
        <Link to="/rfq" className="inline-flex items-center gap-2 text-sm text-[#0dd3ce] uppercase tracking-[0.2em] hover:text-[#5ce7e3]" data-testid="capabilities-cta">
          Submit Drawing <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {CAPABILITIES.map(({ icon: Icon, title, desc, img }, i) => (
          <div
            key={i}
            data-testid={`capability-card-${i}`}
            className="group relative overflow-hidden border border-[#0a3a47] bg-[#0a3845] hover:bg-[#0e4555] hover:border-[#0dd3ce]/50 transition-all duration-300"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img src={img} alt={title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
            </div>
            <div className="p-8">
              <Icon className="w-6 h-6 text-[#0dd3ce] mb-4" />
              <h3 className="font-display text-xl font-semibold text-slate-100 mb-3">{title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-section" className="px-6 md:px-12 lg:px-20 py-24 max-w-7xl mx-auto">
    <Overline>Why procurement teams partner with us</Overline>
    <div className="grid md:grid-cols-2 gap-12 items-start">
      <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-100 tracking-tight leading-[1.05]">
        Old-school discipline.<br/>
        <span className="text-[#0dd3ce]">Global-grade execution.</span>
      </h2>
      <div className="space-y-6 text-slate-400">
        {[
          ["Decades of moulding intuition", "There is no substitute for knowing how a polymer truly behaves at 4am, mid-shift."],
          ["Single-point accountability", "Direct line to ownership. No B2B vendor merry-go-round."],
          ["Built for high-tonnage scale", "Our industrial Gala expansion was engineered for repeatable, high-volume B2B output."],
          ["Documentation that holds up", "Material certs, dimensional reports, traceability — the way modern global supply chains demand."],
        ].map(([t, d], i) => (
          <div key={i} className="flex gap-4 border-b border-[#0a3a47] pb-6">
            <div className="font-display text-[#0dd3ce] text-2xl font-bold w-10">0{i+1}</div>
            <div>
              <div className="text-slate-100 font-semibold">{t}</div>
              <div className="text-sm text-slate-500 mt-1">{d}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section data-testid="cta-section" className="px-6 md:px-12 lg:px-20 py-24">
    <div className="max-w-5xl mx-auto border border-[#0a3a47] bg-[#0a3845] p-10 md:p-16 relative overflow-hidden">
      <div className="absolute -top-12 -right-12 w-64 h-64 bg-[#0dd3ce]/10 blur-3xl rounded-full" />
      <Overline>Start your spec review</Overline>
      <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-100 max-w-3xl">
        Send us your drawing. We'll come back with material guidance, tooling thoughts, and a clean quote.
      </h2>
      <Link to="/rfq" data-testid="bottom-cta-rfq" className="mt-8 inline-flex items-center gap-3 bg-[#0dd3ce] text-[#03161D] font-semibold px-7 py-4 hover:bg-[#5ce7e3] transition-colors uppercase tracking-wider text-sm">
        Request a Quote <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#062632] text-slate-200">
      <Header />
      <main className="fade-up">
        <Hero />
        <Legacy />
        <Capabilities />
        <WhyUs />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}
